import { z } from 'zod';
import { id, isoDate } from './common';

/**
 * Offline sync contracts shared by the mobile field app, the per-project sync Durable Object
 * (apps/workers/src/sync) and the merge engine (packages/sync).
 *
 * Transport: `https://<sync host>/sync/<projectId>/<route>` (see `SYNC_ROUTES`). Every request
 * carries `Authorization: Bearer <Better Auth session token>`.
 */

/** Entities the mobile field app can edit offline. */
export const SYNC_ENTITIES = [
  'daily_log',
  'photo',
  'punch_item',
  'task',
  'rfi',
  'markup',
  'activity',
  'schedule_task',
] as const;
export const syncEntity = z.enum(SYNC_ENTITIES);
export type SyncEntity = z.infer<typeof syncEntity>;

/** Entities the server accepts pushed changes for (the rest are pulled read-only). */
export const SYNC_WRITABLE_ENTITIES = ['daily_log', 'photo', 'punch_item', 'rfi', 'markup'] as const satisfies readonly SyncEntity[];
export type SyncWritableEntity = (typeof SYNC_WRITABLE_ENTITIES)[number];

/** Entities a device downloads for offline use. */
export const SYNC_PULL_ENTITIES = ['daily_log', 'photo', 'punch_item', 'rfi', 'markup'] as const satisfies readonly SyncEntity[];
export type SyncPullEntity = (typeof SYNC_PULL_ENTITIES)[number];

export const SYNC_OPS = ['insert', 'update', 'delete'] as const;
export const syncOp = z.enum(SYNC_OPS);
export type SyncOp = z.infer<typeof syncOp>;

/**
 * Hybrid logical clock timestamp, encoded so that string order equals causal order:
 * `<wall ms, 13 digits>-<counter, 5 digits>-<node id>`, e.g. `1760300000000-00003-dev_ab12`.
 */
export const hlcString = z
  .string()
  .max(160)
  .regex(/^\d{13}-\d{5}-[A-Za-z0-9_.:-]{1,128}$/, 'Invalid HLC timestamp');

/** A single change recorded on a device (or server), the unit of the offline change log. */
export const syncChange = z.object({
  /** Client-generated UUID v7 for idempotency. */
  changeId: id,
  entity: syncEntity,
  entityId: id,
  projectId: id,
  op: syncOp,
  /** Field values for insert, or the changed fields for update. */
  data: z.record(z.string(), z.unknown()),
  /** The server `version` the client based this edit on (0 for inserts). */
  baseVersion: z.number().int().nonnegative(),
  clientTimestamp: isoDate,
  deviceId: z.string().min(1).max(128),
  /** HLC of the edit. Drives per-field last-writer-wins on the server. */
  hlc: hlcString.optional(),
  /**
   * Values of the changed fields as the device last saw them from the server (3-way merge base).
   * A field whose server value still equals its base value was not edited concurrently.
   */
  baseValues: z.record(z.string(), z.unknown()).optional(),
});
export type SyncChange = z.infer<typeof syncChange>;

export const syncPushInput = z.object({
  projectId: id,
  deviceId: z.string().min(1).max(128),
  changes: z.array(syncChange).max(500),
});
export type SyncPushInput = z.infer<typeof syncPushInput>;

/** How a concurrently edited field was resolved. */
export const syncFieldConflict = z.object({
  field: z.string(),
  clientValue: z.unknown(),
  serverValue: z.unknown(),
  winner: z.enum(['client', 'server', 'merged']),
});
export type SyncFieldConflict = z.infer<typeof syncFieldConflict>;

export const SYNC_RESULTS = ['applied', 'merged', 'conflict', 'rejected', 'duplicate'] as const;
export const syncResultStatus = z.enum(SYNC_RESULTS);
export type SyncResultStatus = z.infer<typeof syncResultStatus>;

export const syncPushResultItem = z.object({
  changeId: id,
  status: syncResultStatus,
  serverVersion: z.number().int().nullable(),
  message: z.string().optional(),
  /** On conflict/merge, the authoritative server record after resolution. */
  record: z.record(z.string(), z.unknown()).optional(),
  /** Field-level conflict details (merged / conflict results). */
  conflicts: z.array(syncFieldConflict).optional(),
  /**
   * The insert was folded into an existing record (e.g. a second daily log for the same day);
   * the device must replace `entityId` with this id everywhere.
   */
  remappedId: id.optional(),
});
export type SyncPushResultItem = z.infer<typeof syncPushResultItem>;

export const syncPushResult = z.object({
  results: z.array(syncPushResultItem),
  cursor: z.string(),
});
export type SyncPushResult = z.infer<typeof syncPushResult>;

export const syncPullInput = z.object({
  projectId: id,
  /** Opaque server cursor from the last pull; omit for a full snapshot. */
  cursor: z.string().max(256).optional().nullable(),
  entities: z.array(syncEntity).optional(),
  limit: z.number().int().min(1).max(2000).default(500),
});
export type SyncPullInput = z.infer<typeof syncPullInput>;

export const syncPulledChange = z.object({
  entity: syncEntity,
  entityId: id,
  op: syncOp,
  version: z.number().int(),
  record: z.record(z.string(), z.unknown()).nullable(),
});
export type SyncPulledChange = z.infer<typeof syncPulledChange>;

export const syncPullResult = z.object({
  changes: z.array(syncPulledChange),
  cursor: z.string(),
  hasMore: z.boolean(),
});
export type SyncPullResult = z.infer<typeof syncPullResult>;

/**
 * Server operation log entry, stored in `sync_change.data` for every accepted change.
 * `patch` is what was written; `fieldClocks` the HLC per written field (used for later
 * last-writer-wins decisions); `conflicts` what was overwritten or dropped.
 */
export const syncLogData = z.object({
  patch: z.record(z.string(), z.unknown()),
  fieldClocks: z.record(z.string(), hlcString),
  conflicts: z.array(syncFieldConflict).default([]),
  /** Set on alias rows: the client id was folded into this canonical record id. */
  aliasOf: id.optional(),
});
export type SyncLogData = z.infer<typeof syncLogData>;

// ---------------------------------------------------------------------------
// Directory + project bootstrap (reference data for offline use)
// ---------------------------------------------------------------------------

export const syncDirectoryResult = z.object({
  user: z.object({ id, name: z.string(), email: z.string() }),
  workspaces: z.array(z.object({ id, name: z.string(), slug: z.string(), role: z.string() })),
  projects: z.array(
    z.object({
      id,
      workspaceId: id,
      number: z.string(),
      name: z.string(),
      status: z.string(),
      addressLine: z.string().nullable(),
      role: z.string(),
      updatedAt: isoDate,
    }),
  ),
});
export type SyncDirectoryResult = z.infer<typeof syncDirectoryResult>;

/** Tile pyramid produced for a drawing revision (`<revision key prefix>/tiles/manifest.json`). */
export const syncTileManifest = z.object({
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  tileSize: z.number().int().positive(),
  format: z.enum(['png', 'jpg', 'webp']),
  levels: z
    .array(
      z.object({
        /** Zoom level; 0 is the smallest image. */
        z: z.number().int().nonnegative(),
        /** Scale relative to the full-resolution sheet (0 < scale <= 1). */
        scale: z.number().positive().max(1),
        cols: z.number().int().positive(),
        rows: z.number().int().positive(),
      }),
    )
    .min(1),
});
export type SyncTileManifest = z.infer<typeof syncTileManifest>;

export const syncDrawing = z.object({
  id,
  number: z.string(),
  title: z.string(),
  discipline: z.string(),
  setName: z.string().nullable(),
  version: z.number().int(),
  revision: z
    .object({
      id,
      revision: z.string(),
      r2Key: z.string(),
      thumbnailKey: z.string().nullable(),
      widthPx: z.number().int().nullable(),
      heightPx: z.number().int().nullable(),
      /** Key prefix for tiles: `<tilesPrefix>/<z>/<x>_<y>.<format>`. Null when no tiles exist. */
      tilesPrefix: z.string().nullable(),
      tiles: syncTileManifest.nullable(),
    })
    .nullable(),
});
export type SyncDrawing = z.infer<typeof syncDrawing>;

export const syncProjectPermissions = z.object({
  dailyLog: z.object({ create: z.boolean(), update: z.boolean() }),
  punchItem: z.object({ create: z.boolean(), update: z.boolean(), delete: z.boolean() }),
  rfi: z.object({ create: z.boolean(), update: z.boolean() }),
  photo: z.object({ create: z.boolean(), update: z.boolean() }),
  markup: z.object({ create: z.boolean(), update: z.boolean() }),
});
export type SyncProjectPermissions = z.infer<typeof syncProjectPermissions>;

export const syncBootstrapResult = z.object({
  project: z.object({
    id,
    workspaceId: id,
    number: z.string(),
    name: z.string(),
    status: z.string(),
    addressLine: z.string().nullable(),
    version: z.number().int(),
  }),
  role: z.string(),
  userId: id,
  permissions: syncProjectPermissions,
  members: z.array(z.object({ userId: id, name: z.string(), role: z.string() })),
  companies: z.array(z.object({ id, name: z.string(), trades: z.array(z.string()) })),
  drawings: z.array(syncDrawing),
});
export type SyncBootstrapResult = z.infer<typeof syncBootstrapResult>;

// ---------------------------------------------------------------------------
// Resumable uploads (R2 multipart through the sync Durable Object)
// ---------------------------------------------------------------------------

/** Minimum R2 multipart part size (every part except the last). */
export const SYNC_UPLOAD_PART_SIZE = 5 * 1024 * 1024;
export const SYNC_UPLOAD_MAX_BYTES = 200 * 1024 * 1024;
export const SYNC_UPLOAD_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp'] as const;

export const syncCreateUploadInput = z.object({
  projectId: id,
  /** Client id of the photo record this file belongs to (becomes part of the key). */
  photoId: id,
  contentType: z.enum(SYNC_UPLOAD_CONTENT_TYPES),
  size: z.number().int().positive().max(SYNC_UPLOAD_MAX_BYTES),
});
export type SyncCreateUploadInput = z.infer<typeof syncCreateUploadInput>;

export const syncCreateUploadResult = z.object({
  key: z.string(),
  uploadId: z.string(),
  partSize: z.number().int().positive(),
});
export type SyncCreateUploadResult = z.infer<typeof syncCreateUploadResult>;

export const syncUploadPart = z.object({ partNumber: z.number().int().min(1).max(10_000), etag: z.string().min(1) });
export type SyncUploadPart = z.infer<typeof syncUploadPart>;

export const syncCompleteUploadInput = z.object({
  key: z.string().min(1).max(1024),
  uploadId: z.string().min(1).max(1024),
  parts: z.array(syncUploadPart).min(1).max(10_000),
});
export type SyncCompleteUploadInput = z.infer<typeof syncCompleteUploadInput>;

export const syncAbortUploadInput = z.object({ key: z.string().min(1).max(1024), uploadId: z.string().min(1).max(1024) });

// ---------------------------------------------------------------------------
// WebSocket
// ---------------------------------------------------------------------------

/** Messages over the per-project Durable Object WebSocket. */
export const syncSocketMessage = z.discriminatedUnion('type', [
  z.object({ type: z.literal('hello'), projectId: id, cursor: z.string().nullable() }),
  z.object({ type: z.literal('changed'), projectId: id, cursor: z.string() }),
  z.object({ type: z.literal('presence'), userIds: z.array(id) }),
  z.object({ type: z.literal('ping') }),
  z.object({ type: z.literal('pong') }),
  /** Client -> server: push changes over the socket (same semantics as POST /push). */
  z.object({ type: z.literal('push'), requestId: z.string().max(64), input: syncPushInput }),
  /** Server -> client: result of a socket push. */
  z.object({ type: z.literal('push_result'), requestId: z.string().max(64), result: syncPushResult }),
  z.object({ type: z.literal('error'), requestId: z.string().max(64).optional(), code: z.string(), message: z.string() }),
]);
export type SyncSocketMessage = z.infer<typeof syncSocketMessage>;

/** Paths served by the per-project sync Durable Object (after `/sync/<projectId>`). */
export const SYNC_ROUTES = {
  directory: '/directory',
  bootstrap: '/bootstrap',
  push: '/push',
  pull: '/pull',
  connect: '/connect',
  notify: '/notify',
  file: '/file',
  uploads: '/uploads',
  uploadPart: '/uploads/part',
  uploadComplete: '/uploads/complete',
  uploadAbort: '/uploads/abort',
} as const;
